import { RecipePreview } from '@xoxokiosk/shared/components/RecipePreview';
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { KioskInstanceResponse, ORDER_STATUS_OPTIONS, ORDER_SIZE_OPTIONS } from '@xoxokiosk/shared';
import { shopApi, Shop, orderDefaultsApi, kioskInstanceApi, kioskTypeApi, recipeApi, xoxoRecipeApi, translationApi, downloadPhotowall, clearPhotowall } from '@xoxokiosk/shared';
import { toKebabCase } from '../utils/snakeCase';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Input, Badge, Table, TableBody, TableCell, TableHead, TableHeader, TableRow, Alert, AlertDescription, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Skeleton } from '@xoxokiosk/ui';
import { ArrowLeft, Plus, Trash2, Download, Languages, Image as ImageIcon, ExternalLink, AlertCircle } from 'lucide-react';

interface KioskType {
  id: string;  // This should match what the API returns
  config_identifier: string;
  name: string;
  description: string;
}  // Add other properties that might exist on the kiosk type object



const ShopDashboardPage: React.FC = () => {

  const { shopId } = useParams<{ shopId: string }>();
  const [shop, setShop] = useState<Shop | null>(null);
  const [kioskTypes, setKioskTypes] = useState<KioskType[]>([]);
  const [instances, setInstances] = useState<KioskInstanceResponse[]>([]);
  const [selectedKioskType, setSelectedKioskType] = useState<string>('');
  const [newInstanceName, setNewInstanceName] = useState('');
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  // Order defaults for this shop (editable)
  const [orderDefaults, setOrderDefaults] = useState<{ quantity: number; size: string; status: string } | null>(null);
  const [orderDefaultsError, setOrderDefaultsError] = useState('');
  const [orderDefaultsSuccess, setOrderDefaultsSuccess] = useState('');
  const orderStatusEnum = ORDER_STATUS_OPTIONS;
  const orderSizeEnum = ORDER_SIZE_OPTIONS;
  const navigate = useNavigate();
  // Recipes state
  const [recipesStatus, setRecipesStatus] = useState('');
  const [recipePreviewKey, setRecipePreviewKey] = useState(0);
  
  // Translation state
  const [translationStatus, setTranslationStatus] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [supportedLanguages, setSupportedLanguages] = useState<Array<{code: string, name: string, nativeName: string}>>([]);
  const [translationJobs, setTranslationJobs] = useState<any[]>([]);

  // Photowall admin state
  const [photowallStatus, setPhotowallStatus] = useState('');

  // Fetch and save all recipes for this shop using the backend endpoint
  const handleFetchRecipes = async () => {
    setRecipesStatus('Fetching and saving recipes...');
    try {
      if (!shopId) return;
      // Use the backend endpoint to fetch and update recipes in one go
      const response = await shopApi.fetchAndUpdateShopRecipes(shopId);
      const { recipes } = response.data;
      if (Array.isArray(recipes) && recipes.length > 0) {
        setRecipesStatus(`Fetched and saved ${recipes.length} recipes.`);
      } else {
        setRecipesStatus('No recipes found for this shop.');
      }
      setRecipePreviewKey(k => k + 1); // trigger RecipePreview refresh
    } catch (error: any) {
      setRecipesStatus('Failed to fetch and save recipes.');
      console.error('Error in handleFetchRecipes:', error);
      setRecipePreviewKey(k => k + 1); // still trigger refresh in case
    }
  };

  // (Moved) MBTI bar explanations generation lives in StoryTreeAdminPage

  // Translation functions
  const handleGenerateTranslations = async () => {
    if (!shopId || !selectedLanguage) {
      alert('Please select a language for translation.');
      return;
    }

    setTranslationStatus('Starting translation job...');
    try {
      const response = await translationApi.generateTranslations(shopId, selectedLanguage);
      setTranslationStatus(`Translation job started for ${selectedLanguage}. This may take a few minutes.`);
      
      // Refresh job status
      fetchTranslationJobs();
      
      // Set up polling to check job status
      const pollInterval = setInterval(async () => {
        try {
          const jobsResponse = await translationApi.getJobStatus(shopId, selectedLanguage);
          const jobs = jobsResponse.data?.jobs || [];
          const latestJob = jobs[0];
          
          if (latestJob && (latestJob.status === 'completed' || latestJob.status === 'failed')) {
            clearInterval(pollInterval);
            if (latestJob.status === 'completed') {
              setTranslationStatus(`Translation completed successfully! Translated ${latestJob.completed_items}/${latestJob.total_items} items.`);
              setRecipePreviewKey(k => k + 1); // Refresh recipe preview
            } else {
              setTranslationStatus(`Translation failed: ${latestJob.error_message || 'Unknown error'}`);
            }
            fetchTranslationJobs();
          } else if (latestJob) {
            setTranslationStatus(`Translation in progress: ${latestJob.completed_items}/${latestJob.total_items} items (${latestJob.progress_pct.toFixed(1)}%)`);
          }
        } catch (error) {
          console.error('Error polling translation status:', error);
        }
      }, 5000); // Poll every 5 seconds
      
      // Clear polling after 10 minutes to prevent infinite polling
      setTimeout(() => {
        clearInterval(pollInterval);
      }, 600000);
      
    } catch (error: any) {
      console.error('Error starting translation:', error);
      if (error.response?.status === 409) {
        setTranslationStatus('Translation job already in progress for this language.');
      } else {
        setTranslationStatus('Failed to start translation job.');
      }
    }
  };

  const fetchTranslationJobs = async () => {
    if (!shopId) return;
    try {
      const response = await translationApi.getJobStatus(shopId);
      setTranslationJobs(response.data?.jobs || []);
    } catch (error) {
      console.error('Error fetching translation jobs:', error);
    }
  };

  const fetchSupportedLanguages = async () => {
    try {
      const response = await translationApi.getSupportedLanguages();
      setSupportedLanguages(response.data?.supportedLanguages || []);
    } catch (error) {
      console.error('Error fetching supported languages:', error);
    }
  };

  // Save order defaults for the shop
  const handleSaveOrderDefaults = async () => {
    setOrderDefaultsError('');
    if (!shopId || !orderDefaults) return;

    try {
      const response = await orderDefaultsApi.update(shopId, orderDefaults);
      setOrderDefaultsSuccess('Order defaults saved successfully');
      setOrderDefaultsError('');
      setTimeout(() => setOrderDefaultsSuccess(''), 3000);
    } catch (error) {
      console.error('Failed to save order defaults:', error);
      setOrderDefaultsError('Failed to save order defaults');
      setOrderDefaultsSuccess('');
    }
  };

  useEffect(() => {
    const fetchShopDetails = async () => {
      if (!shopId) return;
      try {
        // A dedicated endpoint would be better, but for now we filter from all shops
        const { data: fetchedShops } = await shopApi.getAllShops();
        console.log('[fetchShopDetails] shopId:', shopId);
        console.log('[fetchShopDetails] fetchedShops:', fetchedShops);
        const currentShop = fetchedShops.find((s: Shop) => s.id === shopId || s.shop_id === shopId);
        console.log('[fetchShopDetails] currentShop:', currentShop);
        setShop(currentShop || null);
      } catch (error) {
        console.error('Failed to fetch shop details:', error);
      }
    };

    const fetchKioskTypes = async () => {
      try {
        const response = await kioskTypeApi.getKioskTypes();
        // Handle both direct array response and Axios response format
        const kioskTypesData = Array.isArray(response) ? response : 
                             (response.data ? (Array.isArray(response.data) ? response.data : []) : []);
        
        console.log('Fetched kiosk types:', kioskTypesData);
        if (kioskTypesData.length > 0) {
          console.log('First kiosk type:', kioskTypesData[0]);
          console.log('First kiosk type properties:', Object.keys(kioskTypesData[0]));
        } else {
          console.warn('No kiosk types returned from the API');
        }
        setKioskTypes(kioskTypesData);
      } catch (error) {
        console.error('Failed to fetch kiosk types:', error);
      }
    };

    // Fetch order defaults for reference
    const fetchOrderDefaults = async () => {
      console.log('Fetching order defaults for shop:', shopId);
      if (!shopId) return;
      
      try {
        const data = await orderDefaultsApi.get(shopId);
        console.log('Fetched order defaults:', data);
        setOrderDefaults({
          quantity: data.quantity || 1,
          size: data.size || 'sizeM',
          status: data.status || 'PENDING'
        });
      } catch (error) {
        console.error('Failed to fetch order defaults:', error);
        // Set defaults if not found
        setOrderDefaults({
          quantity: 1,
          size: 'sizeM',
          status: 'PENDING'
        });
      }
    };

    fetchShopDetails();
    fetchKioskTypes();
    fetchOrderDefaults();
    fetchSupportedLanguages();
    fetchTranslationJobs();
  }, [shopId]);

  useEffect(() => {
    const fetchKioskInstances = async (id: string) => {
      try {
        const { data: fetchedInstances } = await kioskInstanceApi.getKioskInstancesByShop(id);
        setInstances(fetchedInstances);
      } catch (error) {
        console.error('Failed to fetch kiosk instances:', error);
        setInstances([]);
      }
    };

    if (shopId) {
      fetchKioskInstances(shopId);
    } else {
      setInstances([]);
    }
  }, [shopId, refreshTrigger]);

  const handleDeleteInstance = async (joinCode: string) => {
    if (window.confirm(`Are you sure you want to permanently delete kiosk instance #${joinCode}? This action cannot be undone.`)) {
      try {
        await kioskInstanceApi.deleteKioskInstance(joinCode);
        alert('Kiosk instance deleted successfully.');
        setRefreshTrigger(t => t + 1); // Refresh the list
      } catch (error) {
        console.error('Failed to delete kiosk instance:', error);
        alert('Failed to delete kiosk instance. Please check the console for details.');
      }
    }
  };

  const handleLaunchKiosk = async () => {
    if (!shopId || !selectedKioskType) {
      alert('Please select a kiosk type.');
      return;
    }

    try {
      console.log('Selected kiosk type config_identifier:', selectedKioskType);
      console.log('Available kiosk types:', kioskTypes);
      
      // Find the selected kiosk type by config_identifier
      const selectedType = kioskTypes.find(type => type.config_identifier === selectedKioskType);
      
      if (!selectedType) {
        console.error('Selected kiosk type not found in:', kioskTypes);
        alert(`Kiosk type "${selectedKioskType}" not found. Please select a valid kiosk type.`);
        return;
      }
      
      console.log('Selected kiosk type details:', selectedType);
      
      // Use the config_identifier for the kiosk_type_identifier
      const { data: newInstance } = await kioskInstanceApi.createKioskInstance({ 
        shop_id: shopId, 
        kiosk_type_identifier: selectedType.config_identifier,
        name: newInstanceName || `New ${selectedType.name}`
      });
      
      if (newInstance && newInstance.join_code) {
        const joinCode = newInstance.join_code;
        alert(`✅ Success! New kiosk instance created with Join Code: ${joinCode}\n
Kiosk Type: ${selectedType.name} (${selectedType.config_identifier})\nInstance Name: ${newInstanceName || `New ${selectedType.name}`}\n\nYou can now access this kiosk using the join code.`);
        
        // Copy the join code to clipboard
        try {
          await navigator.clipboard.writeText(joinCode);
          console.log('Join code copied to clipboard');
        } catch (err) {
          console.warn('Failed to copy join code to clipboard:', err);
        }
      } else {
        throw new Error('Failed to get instance ID from the response');
      }
      
      setNewInstanceName(''); // Clear the input field
      setSelectedKioskType(''); // Reset the selection
      setRefreshTrigger(t => t + 1);
    } catch (error) {
      console.error('Failed to launch kiosk instance:', error);
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
      alert(`❌ Failed to launch kiosk instance: ${errorMessage}\n\nPlease check the console for more details.`);
      console.error('Failed to launch kiosk instance:', error);
      alert('Failed to launch kiosk instance. Please check the console for details.');
    }
  };

  // Photowall admin handlers
  const handleDownloadPhotowall = async () => {
    if (!shopId) return;
    
    setPhotowallStatus('Downloading photowall...');
    try {
      const response = await downloadPhotowall(shopId);
      
      // Create download link for the ZIP file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `photowall-${shopId}-${new Date().toISOString().split('T')[0]}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setPhotowallStatus('Photowall downloaded successfully!');
    } catch (error: any) {
      setPhotowallStatus('Failed to download photowall.');
      console.error('Error downloading photowall:', error);
    }
  };

  const handleClearPhotowall = async () => {
    if (!shopId) return;
    
    if (!window.confirm('Are you sure you want to permanently delete ALL photowall media and linked selfies for this shop? This action cannot be undone.')) {
      return;
    }
    
    setPhotowallStatus('Clearing photowall...');
    try {
      const result = await clearPhotowall(shopId);
      setPhotowallStatus(`Successfully cleared photowall! Deleted ${result.photowallItems} photowall items, ${result.totalItems} total items, and ${result.s3FilesDeleted} S3 files.`);
    } catch (error: any) {
      setPhotowallStatus('Failed to clear photowall.');
      console.error('Error clearing photowall:', error);
    }
  };

  if (!shop) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto p-6 space-y-6">
          <div className="flex items-center gap-4">
            <Skeleton className="size-10 rounded-md" />
            <div className="space-y-2">
              <Skeleton className="h-8 w-48" />
              <Skeleton className="h-4 w-32" />
            </div>
          </div>
          <Skeleton className="h-48 w-full rounded-lg" />
          <Skeleton className="h-48 w-full rounded-lg" />
        </div>
      </div>
    );
  }

  const handleDeleteAllInstances = async () => {
    if (!window.confirm('Are you sure you want to permanently delete ALL kiosk instances? This action cannot be undone.')) {
      return;
    }
    try {
      const { data: instances } = await kioskInstanceApi.getAllKioskInstances();
      if (!Array.isArray(instances) || instances.length === 0) {
        alert('No instances to delete.');
        return;
      }
      
      // Filter out any instances that don't have a join_code
      const instancesToDelete = instances.filter((instance: any) => instance.join_code);
      
      if (instancesToDelete.length === 0) {
        alert('No valid instances to delete (missing join_code).');
        return;
      }
      
      await Promise.all(instancesToDelete.map((instance: any) => 
        kioskInstanceApi.deleteKioskInstance(instance.join_code)
      ));
      
      alert(`Successfully deleted ${instancesToDelete.length} kiosk instances.`);
      setRefreshTrigger(t => t + 1); // Refresh the list
    } catch (error) {
      console.error('Failed to delete kiosk instances:', error);
      alert('Failed to delete kiosk instances. Please check the console for details.');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/shop-admin')}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-4xl font-bold tracking-tight">{shop.name}</h1>
                <p className="text-muted-foreground mt-1">Shop Dashboard & Configuration</p>
              </div>
            </div>
          </div>
          <Button onClick={() => navigate(`/shops/${shopId}/master`)}>
            <ExternalLink className="mr-2 h-4 w-4" />
            Shop Master Page
          </Button>
        </div>

        {/* Order Defaults Card */}
        <Card>
          <CardHeader>
            <CardTitle>Order Defaults</CardTitle>
            <CardDescription>Configure default order settings for this shop</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {orderDefaults ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Quantity</label>
                    <Input
                      type="number"
                      min={1}
                      value={orderDefaults.quantity}
                      onChange={e => setOrderDefaults({ ...orderDefaults, quantity: Number(e.target.value) })}
                      className="w-full"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Size</label>
                    <Select value={orderDefaults.size} onValueChange={(value) => value && setOrderDefaults({ ...orderDefaults, size: value })}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select size" />
                      </SelectTrigger>
                      <SelectContent>
                        {orderSizeEnum.map(size => (
                          <SelectItem key={size} value={size}>{size}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Status</label>
                    <Select value={orderDefaults.status} onValueChange={(value) => value && setOrderDefaults({ ...orderDefaults, status: value })}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        {orderStatusEnum.map(status => (
                          <SelectItem key={status} value={status}>{status}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex flex-wrap gap-3">
                  <Button onClick={handleSaveOrderDefaults}>Save Order Defaults</Button>
                  <Button onClick={handleFetchRecipes} variant="secondary">Fetch All Recipes</Button>
                </div>
                {orderDefaultsError && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{orderDefaultsError}</AlertDescription>
                  </Alert>
                )}
                {orderDefaultsSuccess && (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{orderDefaultsSuccess}</AlertDescription>
                  </Alert>
                )}
                {recipesStatus && (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{recipesStatus}</AlertDescription>
                  </Alert>
                )}
              </>
            ) : (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>Could not load order defaults for this shop.</AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Translation Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Languages className="h-5 w-5" />
              Recipe Translation
            </CardTitle>
            <CardDescription>Generate translations for recipes in different languages</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-3 items-end">
              <div className="space-y-2 flex-1 min-w-[200px]">
                <label className="text-sm font-medium">Target Language</label>
                <Select value={selectedLanguage} onValueChange={(value) => setSelectedLanguage(value || '')}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select Language" />
                  </SelectTrigger>
                  <SelectContent>
                    {supportedLanguages.map((lang) => (
                      <SelectItem key={lang.code} value={lang.code}>
                        {lang.name} ({lang.nativeName})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button 
                onClick={handleGenerateTranslations} 
                disabled={!selectedLanguage}
              >
                Generate Translations
              </Button>
            </div>

            {translationStatus && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{translationStatus}</AlertDescription>
              </Alert>
            )}

            {translationJobs.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-sm font-semibold">Recent Translation Jobs</h3>
                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                  {translationJobs.slice(0, 5).map((job) => {
                    const metadata = job.metadata_json || {};
                    const languageName = supportedLanguages.find(l => l.code === metadata.target_language)?.name || metadata.target_language;
                    
                    return (
                      <div key={job.id} className="p-3 bg-muted rounded-md text-sm">
                        <div className="flex justify-between items-center">
                          <span className="font-medium">
                            {languageName} - <Badge variant={job.status === 'completed' ? 'default' : 'secondary'}>{job.status.toUpperCase()}</Badge>
                          </span>
                          <span className="text-muted-foreground">
                            {job.completed_items}/{job.total_items} ({job.progress_pct.toFixed(1)}%)
                          </span>
                        </div>
                        {job.error_message && (
                          <p className="text-destructive text-xs mt-1">Error: {job.error_message}</p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">
                          Started: {new Date(job.started_at).toLocaleString()}
                          {job.completed_at && ` • Completed: ${new Date(job.completed_at).toLocaleString()}`}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Photowall Admin Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ImageIcon className="h-5 w-5" />
              Photowall Management
            </CardTitle>
            <CardDescription>Download or clear photowall media for this shop</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-3">
              <Button onClick={handleDownloadPhotowall} variant="default">
                <Download className="mr-2 h-4 w-4" />
                Download All Photowall Media
              </Button>
              <Button onClick={handleClearPhotowall} variant="destructive">
                <Trash2 className="mr-2 h-4 w-4" />
                Clear All Photowall Media
              </Button>
            </div>
            {photowallStatus && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{photowallStatus}</AlertDescription>
              </Alert>
            )}
            <div className="text-xs text-muted-foreground space-y-1">
              <p>• Download: Creates a ZIP file with all photowall images and videos directly from S3</p>
              <p>• Clear: Permanently deletes all photowall media, linked selfies, and S3 files for this shop</p>
            </div>
          </CardContent>
        </Card>

        {/* Launch New Kiosk */}
        <Card>
          <CardHeader>
            <CardTitle>Launch New Kiosk</CardTitle>
            <CardDescription>Create a new kiosk instance for this shop</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Instance Name</label>
                <Input
                  type="text"
                  value={newInstanceName}
                  onChange={(e) => setNewInstanceName(e.target.value)}
                  placeholder="New Instance Name"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Kiosk Type</label>
                <Select value={selectedKioskType} onValueChange={(value) => setSelectedKioskType(value || '')}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select Kiosk Type" />
                  </SelectTrigger>
                  <SelectContent>
                    {kioskTypes.map((type) => (
                      <SelectItem key={type.id} value={type.config_identifier}>
                        {type.name} ({type.config_identifier})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-3">
              <Button onClick={handleLaunchKiosk}>
                <Plus className="mr-2 h-4 w-4" />
                Launch Kiosk
              </Button>
              <Button onClick={handleDeleteAllInstances} variant="destructive">
                <Trash2 className="mr-2 h-4 w-4" />
                Delete All Instances
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Active Kiosk Instances */}
        <Card>
          <CardHeader>
            <CardTitle>Active Kiosk Instances</CardTitle>
            <CardDescription>Manage and monitor kiosk instances for this shop</CardDescription>
          </CardHeader>
          <CardContent>
            {instances.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Join Code</TableHead>
                    <TableHead>Kiosk Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Launched At</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {instances.map((instance) => (
                    <TableRow key={instance.join_code} className="cursor-pointer" onClick={() => navigate(`/kiosk/admin/${instance.join_code}`)}>
                      <TableCell className="font-mono">{instance.join_code}</TableCell>
                      <TableCell>{instance.kiosk_type_identifier}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">{instance.status}</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {new Date(instance.created_at).toLocaleString()}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate(`/kiosk/admin/${instance.join_code}`)}
                          >
                            Open Admin
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleDeleteInstance(instance.join_code)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12">
                <p className="text-muted-foreground">No active kiosk instances for this shop.</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recipes Preview Section */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Recipes</CardTitle>
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium">View in language:</label>
                <Select value={selectedLanguage} onValueChange={(value) => setSelectedLanguage(value || '')}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Original" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Original</SelectItem>
                    {supportedLanguages.map((lang) => (
                      <SelectItem key={lang.code} value={lang.code}>
                        {lang.nativeName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <RecipePreview 
              key={recipePreviewKey} 
              shopId={shopId || ''} 
              showIngredients={true}
              showTranslations={true}
              selectedLanguage={selectedLanguage}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );

  

};

export default ShopDashboardPage;
